export type UserMeta = {
    maxBoards?: number
    currencyType?: string
    boardToolsWalkthrough?: {
        completed: boolean
        id: string
        version: number
    }[]
}

export type UserAgreements = {
    researchProgram?: boolean
    mailingList?: boolean | null
}

export type User = {
    id: string
    firebaseId: string
    name: string
    firstName: string
    lastName: string
    email: string
    userTag: string
    meta: UserMeta
    role: string
    status: string
    stripeId?: string
    createdAt: string
    updatedAt: string
    twoFactorEnabled: boolean
    emailVerified: boolean
    lastActiveAt: string
    schoolUsers: SchoolUser[]
    userSubscriptions: UserSubscription[]
    userAgreements?: UserAgreements
    userFeatureFlags?: UserFeatureFlag[]
    permissions?: UserPermissions
    productUsers?: ProductUser[]
}

export type Invitee = {
    id: string
    verificationCode: string
    expiry: Date
    isDeleted: boolean
    email: string
    userId?: string
    lastSent?: Date

    user?: User
    claims?: Claim[]
    createdAt: Date
}

export type Claim = {
    id: string
    inviteeId: string
    schoolSubscriptionId?: string
    role?: 'ADMIN' | 'USER'
    schoolId?: string
    publisherId?: string
    publisherRole?: 'ADMIN' | 'EDITOR' | 'SALES'

    schoolSubscription?: SchoolSubscription
    invitee?: Invitee
    school?: School
    publisher?: Publisher
}

export type ProductUser = {
    id: string
    couponId: string
    productId: string
    userId: string
    createdAt: Date
    updatedAt: Date

    coupon?: Coupon
    product?: Product
    user?: User
}

export type FeatureFlag = {
    id: string
    key: string
    userFeatureFlags: UserFeatureFlag[]
}

export type FeatureFlagRule = {
    id: string
    featureFlagId: string
    model: string
    field: string
    comparator: string
    value: string
    featureFlag?: FeatureFlag
}

export type UserFeatureFlag = {
    userId: string
    featureFlagId: string
    user: User
    featureFlag: FeatureFlag
}

export type UserPermissions = {
    schools: {
        [schoolId: string]: object
    }
    personal: object
}

export interface School {
    id: string
    name: string
    country: string
    govId?: string
    state?: string
    city?: string
    postalCode?: string
    address?: string
    phone?: string
    email?: string
    stripeId?: string
    domain?: string
    createdAt: Date
    updatedAt: Date
    schoolSubscriptions: SchoolSubscription[]
    schoolUsers: SchoolUser[]
    mfaSchoolRequirement: MfaSchoolRequirement
}

export type SchoolSubscription = {
    id: string
    subscriptionId: string
    schoolId: string
    subscription: Subscription
    seatCount: number
    schoolSubscriptionSeats?: SchoolSubscriptionSeat[]
    createdAt: Date
    updatedAt: Date
}

export const subscriptionTerms = ['MONTHLY', 'YEARLY'] as const
export type TermType = (typeof subscriptionTerms)[number]

export const subscriptionsStatuses = ['ACTIVE', 'INACTIVE', 'CANCELLED', 'COMPLETED'] as const
export type SubscriptionStatus = (typeof subscriptionsStatuses)[number]

export type Subscription = {
    id: string
    name?: string
    startDate: Date
    endDate: Date
    term: TermType
    status: SubscriptionStatus
    createdAt: Date
    updatedAt: Date
    isInternal: boolean
    stripeId?: string
    creatorId: string
    userSubscription?: UserSubscription
    schoolSubscription?: SchoolSubscription
    subscriptionProducts: SubscriptionProduct[]
}

export type SubscriptionProduct = {
    product: Product
    subscription: Subscription
}

export type SchoolSubscriptionSeat = {
    id: string
    schoolSubscriptionId: string
    schoolUserId: string
    schoolSubscription: SchoolSubscription
    schoolUser: SchoolUser
    isDeleted: boolean
    createdAt: Date
    updatedAt: Date
}

export type UserSubscription = {
    id: string
    subscription?: Subscription
    user?: User
    createdAt: Date
    updatedAt: Date
}

export type Product = {
    id: string
    name: string
    label?: string // TODO: remove ? when prisma schema is changed
    slug?: string
    key: string
    description?: string
    stripeId?: string
    isPublic: boolean
    productBundles?: ProductBundle[]
    subscriptions?: Subscription[]
    creatorId?: string
    creator?: Creator
    createdAt: Date
    updatedAt: Date
}

export type SchoolUser = {
    id: string
    role: 'ADMIN' | 'USER'
    user: User
    school: School
    schoolId: string
    schoolSubscriptionSeats: SchoolSubscriptionSeat[]
    createdAt: Date
    updatedAt: Date

    mfaSchoolUserRequirement?: MfaSchoolUserRequirement
}

export type Curriculum = {
    id: string
    name: string
    shortCode: string
    slug: string
    guideType: 'DEFAULT_THREE' | 'DEFAULT_TWO'
}

export type YearLevel = {
    id: string
    name: string
    shortName: string
    curriculumId: string
}

export type ObjectiveCategory = {
    id: string
    name: string
    curriculumId: string
    parentId?: string
    order: number
    child?: ObjectiveCategory[]
    objectives?: Objective[]
}

export type Objective = {
    id: string
    description: string
    code: string
    objectiveCategoryId: string
    yearLevelId: string
    order: number
}

export type WebLink = {
    name: string
    url: string
}

export type Bundle = {
    id: string
    name: string
    description: string
    order: number
    creatorId: string
    meta?: {
        links: WebLink[]
    }
    bundleCurriculums: {
        curriculumId: string
    }[]
    version: number
}

export type CommunityResourceFolder = {
    id: string
    name: string
    description: string
    parentId: string
    createdAt?: Date
    updatedAt?: Date
}

export type BundleResource = {
    bundleId: string
    communityResourceFolderId: string
}

export type ProductBundle = {
    productId: string
    bundleId: string
    product: Product
    bundle: Bundle
}

export type Publisher = {
    id: string
    creatorId: string
    creator?: Creator
    publisherUsers: PublisherUser[]
    country?: string
    state?: string
    city?: string
    zipCode?: string
    address?: string
    phone?: string
    email?: string
    createdAt: Date
    updatedAt: Date
}

export type PublisherUser = {
    id: string
    publisherId: string
    userId: string
    user: User
}

export type Creator = {
    id: string
    name: string
    publisher?: Publisher
    profileUrl: string
}

export type Settings = {
    key: string
    value: string
    type: 'NUMBER' | 'DATETIME' | 'STRING' | 'BOOLEAN'
    createdAt: Date
    updatedAt: Date
}

export type EmailMessage = {
    id: string
    type: 'NODEMAILER' | 'MAILERSEND'
    toAddress: string
    toName: string | null
    fromAddress: string
    fromName: string | null
    subject: string
    messageData: object
    templateId: string | null
    timeSent: Date | null
    attempts: number
    createdAt: Date
    updatedAt: Date
}

export type Coupon = {
    id: string
    code: string
    expiry: Date
    productId: string
    isDeleted: boolean
    timesUsed: number
    createdAt: Date
    updatedAt: Date

    product?: Product
}

export type BillableAction = {
    id: string
    action: string
    resourceType: string
    resourceId: string
    units: number
    costPerUnit: number
    currency: string
    creatorId: string
    isBilled: boolean
    actionedByUserId: string

    createdAt: Date
    updatedAt: Date
}

export type MfaSchoolRequirement = {
    id: string
    mfaLevel: 'ALL' | 'ADMIN_ONLY'
    schoolId: string
    settings: {
        adminLeeway: string
        userLeeway: string
    }
}

export type MfaSchoolUserRequirement = {
    mfaSchoolRequirementId: string
    schoolUserId: string
    requiredAfter: Date

    mfaSchoolRequirement?: MfaSchoolRequirement
}

export type MultiFactorInfo = {
    uid: string
    factorId: string
    displayName?: string | null
    enrollmentTime: string
}
